import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import MonitoringMetadataForm from 'components/partials/insight-modal/monitoring/monitoring-metadata-form';
import ReportMetadataForm from 'components/partials/insight-modal/report/report-metadata-form';
import InsightDetailsForm from 'components/partials/insight-modal/report/insight-details-form';
import AnnotateStep from 'components/partials/insight-modal/components/annotate-step';
import InsightDefaultFooter from 'components/partials/insight-modal/components/insight-default-footer';
import UserAccessStep from 'components/partials/insight-modal/components/user-access-step/user-access-step';

import { setMetadata as setMetadataAction } from 'state/insight/insight.slice';

import {
  INSIGHT_MODAL_CLOSE,
} from 'components/partials/insight-modal/shared/constants';
import type { InsightModalMetadata } from 'components/partials/insight-modal/shared/constants';
import { renderModal } from 'helpers/modal-render';

import type { RootState } from 'state/store';

interface OpenInsightModalArgs {
  metadata?: InsightModalMetadata;
  onClose?: () => void;
  footer?: ReactNode;
}

export const openInsightModal = ({
  metadata,
  onClose,
  footer,
}: OpenInsightModalArgs = {}) => {
  const headerText = 'New insight';

  const handleInsightModalClose = () => {
    window.dispatchEvent(new CustomEvent(INSIGHT_MODAL_CLOSE));
    if (typeof onClose === 'function') onClose();
  };

  renderModal({
    headerText,
    modalClassName: '!max-w-[1000px] !w-[1000px] h-[700px]',
    children: <InsightModalContent metadata={metadata} />,
    footer: footer || <InsightDefaultFooter />,
    stepIndicator: <StepIndicator />,
    onCloseCallback: handleInsightModalClose,
    skipUnmountOnClose: true,
    disableEnterConfirm: true,
    headerSubtext:
      'Provide additional context to highlight key changes, trends, or results. Use notes to explain what is driving the data and what actions may be needed.',
  });
};

const StepIndicator = () => {
  const step = useSelector((s: RootState) => s?.insight?.step || 1);
  const metaFromStore = useSelector(
    (s: RootState) => s?.insight?.metadata || {},
  ) as InsightModalMetadata;

  const totalSteps = metaFromStore?.isReport ? 4 : 3;

  return `Step ${step} of ${totalSteps}`;
};

interface InsightModalContentProps {
  metadata?: InsightModalMetadata;
}

const InsightModalContent = ({ metadata }: InsightModalContentProps) => {
  const step = useSelector((s: RootState) => s?.insight?.step || 1);
  const imageUrl = useSelector((s: RootState) => s?.insight?.imageUrl || null);
  const metaFromStore = useSelector(
    (s: RootState) => s?.insight?.metadata || {},
  ) as InsightModalMetadata;
  const dispatch = useDispatch();

  useEffect(() => {
    if (metadata) {
      dispatch(setMetadataAction(metadata as Record<string, unknown>));
    }
  }, [metadata, dispatch]);

  if (step === 2) {
    if (metaFromStore?.isReport) {
      return <ReportMetadataForm initialMetadata={metaFromStore} />;
    }

    return <MonitoringMetadataForm initialMetadata={metaFromStore} />;
  }

  if (step === 3) {
    if (metaFromStore?.isReport) {
      return <InsightDetailsForm initialMetadata={metaFromStore} />;
    }

    return <UserAccessStep />;
  }

  if (step === 4) {
    if (metaFromStore?.isReport) {
      return <UserAccessStep />;
    }
  }

  return <AnnotateStep imageUrl={imageUrl} />;
};

export default openInsightModal;
